import { cloneDeep, get } from 'lodash';
import {
  allPermissionCategories,
  allTokenPermissionCategories,
  defaultPermissions,
  permissionCategories,
  permissionDependencies, roleGroupings,
} from './constants';

export const filterPermissions = (categories, filter, grouping, intl) => {
  const filtered = [];

  categories.forEach((item) => {
    const copy = { ...item, actions: [] };

    item.actions.forEach((action) => {
      const message = intl.formatMessage({ id: `permissions.${grouping}.${item.name}.${action.name}.title` });
      if (message.toLowerCase().includes(filter.toLowerCase())) {
        copy.actions.push(action);
      }
    });
    if (copy.actions.length > 0) {
      filtered.push(copy);
    }
  });

  return filtered;
};

export const categoriesFromPermissions = (permissions, grouping) => {
  const filtered = [];

  const permissionCategoriesToCheck = grouping === 'tokens' ? allTokenPermissionCategories : allPermissionCategories;

  permissionCategoriesToCheck.forEach((item) => {
    const copy = { ...item, actions: [] };

    item.actions.forEach((action) => {
      if (permissions.find((p) => (p.category === item.name && p.action === action.name))) {
        copy.actions.push(action);
      }
    });
    if (copy.actions.length > 0) {
      filtered.push(copy);
    }
  });

  return filtered;
};

const indexOfPermission = (permissions, category, action) => (
  permissions.findIndex((p) => (p.category === category && p.action === action))
);

const togglePermission = (permissions, category, action) => {
  const newPermissions = [...permissions];
  const index = indexOfPermission(newPermissions, category, action);
  const removed = index >= 0;

  if (removed) {
    newPermissions.splice(index, 1);
  } else {
    newPermissions.push({ category, action });
  }

  return { permissions: newPermissions, removed };
};

const dependentPermissions = (category, action, value) => {
  const path = `${category}.${action}.${value ? 'on' : 'off'}`;
  return get(permissionDependencies, path, []);
};

const updatePermission = (permissions, { category, action, value }) => {
  const index = indexOfPermission(permissions, category, action);

  if (value && index === -1) {
    permissions.push({ category, action });
  } else if (!value && index >= 0) {
    permissions.splice(index, 1);
  }
};

const updateDependentPermissions = (permissions, category, action, value) => {
  const dependencies = dependentPermissions(category, action, value);
  dependencies.forEach((permission) => {
    updatePermission(permissions, permission);
  });
};

export const smartToggleRolePermission = (role, category, action) => {
  const { permissions, removed } = togglePermission(role.permissions, category, action);

  if (role.smartFlag) {
    updateDependentPermissions(permissions, category, action, !removed);
  }

  return permissions;
};

export const smartUpdateRolePermissions = (role, categories, status) => {
  const permissions = cloneDeep(role.permissions);
  const value = status === 'on';

  categories.forEach((category) => {
    category.actions.forEach((action) => {
      updatePermission(permissions, { category: category.name, action: action.name, value });

      if (role.smartFlag) {
        updateDependentPermissions(permissions, category.name, action.name, value);
      }
    });
  });

  return permissions;
};

export const permissionsByAccordionCategory = (role, accordionPermissions) => {
  if (!role.permissions) {
    return [];
  }
  return role.permissions.filter((p1) => (
    accordionPermissions.some((p2) => p1.category === p2.category && p1.action === p2.action)
  ));
};

const convertPermissions = (apiPermissions) => {
  const permissions = {};

  apiPermissions.forEach(({ category, action }) => {
    permissions[category] = permissions[category] || {};
    permissions[category][action] = !!action;
  });

  return permissions;
};

const mergePermissions = (apiPermissions) => {
  const currentUserPermissions = convertPermissions(apiPermissions);
  const mergedPermissions = {};

  Object.entries(defaultPermissions).forEach(([category, actions]) => {
    mergedPermissions[category] = actions;
    if (currentUserPermissions[category]) {
      mergedPermissions[category] = { ...actions, ...currentUserPermissions[category] };
    }
  });

  return mergedPermissions;
};

export const makePermissionsForState = (apiPermissions, approved) => (
  approved ? mergePermissions(apiPermissions) : defaultPermissions
);

export const adminPermissionsCount = ({ metrics }) => (
  metrics.roles_permissions
    + metrics.users_permissions
    + metrics.audit_permissions
    + metrics.vocabularies_permissions
    + metrics.themes_permissions
);

export const allowAdmin = (permissions) => (
  permissions.themes.update
    || permissions.vocabularies.update
    || permissions.roles.read
    || permissions.users.read
    || permissions.collections.read
);

export const permissionsMetrics = ({ permissions }) => ({
  all_permissions: permissions.length,
  roles_permissions: permissions.reduce((n, x) => n + (x.category === permissionCategories.ROLES.name), 0),
  users_permissions: permissions.reduce((n, x) => n + (x.category === permissionCategories.USERS.name), 0),
  audit_permissions: permissions.reduce((n, x) => n + (x.category === permissionCategories.AUDIT.name), 0),
  vocabularies_permissions:
      permissions.reduce((n, x) => n + (x.category === permissionCategories.VOCABULARIES.name), 0),
  themes_permissions: permissions.reduce((n, x) => n + (x.category === permissionCategories.THEMES.name), 0),
});

const permissionDiff = (arrayToCheck, arrayToFind) => {
  const diff = [];
  arrayToCheck.forEach((item) => {
    if (!arrayToFind.find((i) => i.category === item.category && i.action === item.action)) {
      diff.push(item);
    }
  });

  return diff;
};

export const permissionsDiff = (role) => {
  const newPermissions = permissionDiff(role.permissions, role.copy.permissions);
  const deletedPermissions = permissionDiff(role.copy.permissions, role.permissions);

  return [...newPermissions, ...deletedPermissions];
};

export const roleDiff = (role) => {
  const fieldsToCompare = ['name', 'description', 'max_active_tokens', 'max_token_length_days'];
  let diff = false;

  fieldsToCompare.forEach((field) => {
    if (role[field] !== role.copy[field]) {
      diff = true;
    }
  });

  return diff;
};

export const roleManagementAccessControl = ({ permissions }, role) => ({
  allowCreate: permissions.roles.create && role.assignable,
  allowAudit: permissions.roles.audit,
  allowEdit: permissions.roles.update && !role.system,
  allowRoleActionsEdit: permissions.roles.update && role.code !== 'archived' && role.grouping === roleGroupings.USERS,
});

export const roleReassignMessage = (user) => (
  user.role ? 'user.role.reassign.role.text' : 'user.role.reassign.no.role.text'
);

export default (permissions, category, action) => {
  const permission = permissions.find((r) => (r.category === category && r.action === action));
  return !!permission;
};
